import { Button, Select, Textarea, TextInput } from "@mantine/core";
import { IconScale } from "@tabler/icons-react";
import { Modal } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  categories,
  screwSubCategories,
  steelSubCategories,
  type Item,
} from "./data";
import { categoryLabel, subTypeLabel } from "./labels";
import { formatSpec, steelSpecConfigs } from "./steelSpecConfig";
import { calculateSteelWeight } from "./steelWeight";

interface AddItemDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (item: Item) => void;
}

function AddItemDialog({ open, onClose, onAdd }: AddItemDialogProps) {
  const { t } = useTranslation();
  const [categoryId, setCategoryId] = useState("");
  const [subType, setSubType] = useState("");
  const [specValues, setSpecValues] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open) {
      setCategoryId("");
      setSubType("");
      setSpecValues([]);
      setName("");
      setDescription("");
    }
  }, [open]);

  const specConfig =
    categoryId === "steel" && subType ? steelSpecConfigs[subType] : null;

  useEffect(() => {
    if (specConfig) {
      setSpecValues(new Array(specConfig.fields.length).fill(""));
    } else {
      setSpecValues([]);
    }
  }, [subType, specConfig?.fields.length]);

  const updateName = (catId: string, type: string, specs: string[]) => {
    if (!type) return;
    if (catId === "steel") {
      const subCat = steelSubCategories.find((s) => s.id === type);
      if (subCat) {
        const specStr = formatSpec(type, specs);
        const generated = specStr ? `${type}-${specStr}` : subCat.name;
        setName(generated);
      }
    } else if (catId === "screw") {
      const subCat = screwSubCategories.find((s) => s.id === type);
      if (subCat) setName(subCat.name);
    }
  };

  const handleCategoryChange = (value: string | null) => {
    setCategoryId(value || "");
    setSubType("");
    setSpecValues([]);
    setName("");
  };

  const handleSubTypeChange = (value: string | null) => {
    const v = value || "";
    setSubType(v);
    setSpecValues([]);
    updateName(categoryId, v, []);
  };

  const handleSpecValueChange = (index: number, value: string) => {
    const numValue = parseFloat(value);
    if (value !== "" && numValue < 0) return;
    const newValues = [...specValues];
    newValues[index] = value;
    setSpecValues(newValues);
    updateName(categoryId, subType, newValues);
  };

  const specString = useMemo(() => {
    if (categoryId !== "steel" || !subType || specValues.every((v) => !v)) {
      return "";
    }
    return formatSpec(subType, specValues);
  }, [categoryId, subType, specValues]);

  const weightResult = useMemo(() => {
    if (categoryId === "steel" && subType && specString) {
      return calculateSteelWeight(subType, specString);
    }
    return null;
  }, [categoryId, subType, specString]);

  const availableSubTypes =
    categoryId === "steel"
      ? steelSubCategories
      : categoryId === "screw"
        ? screwSubCategories
        : [];

  const isValid = () => {
    if (!categoryId) return false;
    if (categoryId === "steel" || categoryId === "screw") {
      if (!subType) return false;
    }
    if (categoryId === "steel") {
      if (specValues.every((v) => !v)) return false;
    }
    return name.trim() !== "";
  };

  const handleSubmit = () => {
    if (!isValid()) return;
    const category = categories.find((c) => c.id === categoryId);
    const newItem: Item = {
      id:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `item-${Date.now()}`,
      name: name.trim(),
      category: category?.name || "",
      categoryId,
      steelType:
        categoryId === "steel" || categoryId === "screw" ? subType : undefined,
      spec: categoryId === "steel" ? specString : undefined,
      description: description || undefined,
      isActive: true,
    };
    onAdd(newItem);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("itemEditor.addDialog.title")}
      width={520}
      footer={[
        <Button key="cancel" variant="default" onClick={onClose}>
          {t("itemEditor.action.cancel")}
        </Button>,
        <Button key="submit" onClick={handleSubmit} disabled={!isValid()} ml="sm">
          {t("itemEditor.addDialog.submit")}
        </Button>,
      ]}
    >
      <div className="space-y-5 py-4">
        <div className="grid grid-cols-2 gap-4">
          <Select
            label={t("itemEditor.field.category")}
            required
            value={categoryId}
            onChange={handleCategoryChange}
            placeholder={t("itemEditor.addDialog.selectCategory")}
            comboboxProps={{ zIndex: 1100 }}
            data={categories
              .filter((c) => c.id !== "all")
              .map((cat) => ({
                value: cat.id,
                label: categoryLabel(t, cat.id, cat.name),
              }))}
          />

          {(categoryId === "steel" || categoryId === "screw") && (
            <Select
              label={
                categoryId === "steel"
                  ? t("itemEditor.field.steelType")
                  : t("itemEditor.field.screwType")
              }
              required
              value={subType}
              onChange={handleSubTypeChange}
              placeholder={
                categoryId === "steel"
                  ? t("itemEditor.addDialog.selectSteelType")
                  : t("itemEditor.addDialog.selectScrewType")
              }
              comboboxProps={{ zIndex: 1100 }}
              data={availableSubTypes.map((sub) => ({
                value: sub.id,
                label:
                  categoryId === "steel"
                    ? `${sub.id} - ${subTypeLabel(t, categoryId, sub.id, sub.name)}`
                    : subTypeLabel(t, categoryId, sub.id, sub.name),
              }))}
              searchable
              maxDropdownHeight={300}
            />
          )}
        </div>

        {categoryId === "steel" && subType && specConfig && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              {t("itemEditor.field.sectionSpec")} <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              {specConfig.fields.map((field, index) => (
                <div
                  key={field.name}
                  className="flex flex-1 items-center gap-1"
                >
                  <TextInput
                    type="number"
                    min={0}
                    value={specValues[index] || ""}
                    onChange={(e) =>
                      handleSpecValueChange(index, e.currentTarget.value)
                    }
                    placeholder={
                      field.labelKey
                        ? t(`itemEditor.specField.${field.labelKey}`, {
                            defaultValue: field.placeholder,
                          })
                        : field.placeholder
                    }
                    classNames={{ input: "text-center" }}
                    className="flex-1"
                  />
                  {index < specConfig.fields.length - 1 && (
                    <span className="font-medium text-gray-500">X</span>
                  )}
                </div>
              ))}
            </div>
            {specString && (
              <p className="text-sm text-gray-500">
                {t("itemEditor.addDialog.specPreview", { spec: specString })}
              </p>
            )}
          </div>
        )}

        {weightResult && (
          <div className="rounded-lg border border-gray-200 bg-blue-50/50 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900">
              <IconScale size={16} />
              {t("itemEditor.weight.title")}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t("itemEditor.weight.area")}</span>
                <span className="font-mono font-medium">
                  {weightResult.areaMm2.toLocaleString()} mm²
                </span>
              </div>
              <div>
                <span className="text-gray-500">{t("itemEditor.weight.unitWeight")}</span>
                <span className="font-mono font-medium text-blue-600">
                  {weightResult.unitWeightKgM} kg/m
                </span>
              </div>
            </div>
          </div>
        )}

        {categoryId && (
          <>
            <TextInput
              label={t("itemEditor.field.name")}
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder={t("itemEditor.field.namePlaceholder")}
            />

            {/* <TextInput
              label="品項編號"
              value={code}
              onChange={(e) => {
                setCodeEdited(true);
                setCode(e.currentTarget.value);
              }}
              placeholder="輸入品項編號"
            /> */}

            <Textarea
              label={t("itemEditor.field.description")}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              placeholder={t("itemEditor.field.descriptionPlaceholder")}
              minRows={3}
              maxLength={200}
              autosize={false}
              description={`${description.length}/200`}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

export default AddItemDialog;
